import React from 'react';
import {
    Box,
    Text,
    SimpleGrid,
    Spinner,
    Alert,
    AlertIcon,
    VStack,
} from '@chakra-ui/react';
import { useYtgDashboardStats } from '../../hooks/useYtgDashboardStats';
import { YtgStatsCards } from './YtgStatsCards';
import { YtgStatusChart } from './YtgStatusChart';
import { YtgLanguageChart } from './YtgLanguageChart';

/**
 * YTG statistics section for the GLOBAL Dashboard.
 * Contains all the statistics and charts specific to the YTG module.
 *
 * This component encapsulates all the YTG stats display logic,
 * allowing the global Dashboard to stay module-agnostic.
 *
 * IMPORTANT: This component is used by src/pages/Dashboard (GLOBAL Dashboard),
 * not by ytg/pages/Dashboard/ (YTG operations list).
 */
export const YtgStatsSection = (): JSX.Element | null => {
    const { stats, loading, error } = useYtgDashboardStats();

    // Loading state
    if (loading) {
        return (
            <Box p={6} display="flex" alignItems="center" justifyContent="center" minH="200px">
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.ytg.main" thickness="4px" />
                    <Text fontSize="lg" color="gray.600">
                        Chargement des statistiques YTG...
                    </Text>
                </VStack>
            </Box>
        );
    }

    // Error handling
    if (error) {
        return (
            <Box p={6}>
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    Impossible de charger les statistiques YTG.
                </Alert>
            </Box>
        );
    }

    // No data
    if (!stats) return null;

    return (
        <Box>
            {/* Cartes de statistiques principales (already spaced with mb) */}
            <YtgStatsCards stats={stats} />

            {/* Charts */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <YtgStatusChart stats={stats} />
                <YtgLanguageChart stats={stats} />
            </SimpleGrid>
        </Box>
    );
};
